// ARCHIVO COMPARTIDO app/web — editar en ambos repos. Verificar con `npm run check:shared` (TemControlApp).
//
// Cómputo de días de vacaciones consumidos según el `holidayType` del convenio
// del trabajador. Aplica también a otros periodos análogos (bajas médicas).
//
// Dos modalidades:
//
//   'laborables'  → Solo cuentan los días dentro del rango que sean día
//                   laboral del trabajador (su `workhours` tiene `in1` ese
//                   `dayOfWeek`) Y no sean festivos. Findes y festivos no
//                   computan aunque el trabajador los haya solicitado: por
//                   costumbre, el trabajador puede marcar el rango entero
//                   y el sistema descarta los que no se pueden gastar.
//
//   'festivos'    → Vacaciones NATURALES. Cuentan TODOS los días del rango
//                   solicitado (sin importar si son festivos, findes o
//                   descansos del trabajador). Adicionalmente, hay un
//                   "arrastre" después del último día solicitado: si tras
//                   el END del periodo siguen días no laborables (festivos
//                   o días sin slot en el calendario del trabajador),
//                   también cuentan, hasta encontrar el primer día laboral
//                   del trabajador que no sea festivo.
//
// Diseño:
//   - `workHoursByDow` es un Map<dow:0..6, slot> donde `dow` sigue la
//     convención JS (0=Domingo .. 6=Sábado). Slot con `in1` truthy →
//     día laboral del trabajador. Sin slot o sin `in1` → descanso.
//   - `holidaysSet` es un Set<string> de fechas ISO ("YYYY-MM-DD") que
//     son festivos para el trabajador (los devuelve el hook useHolidays).
//   - Se devuelve un Set<string> de ISO para que el caller pueda pintar
//     los días, sumar (`size`), comparar entre periodos, etc.
//   - Los instantes start/end del periodo se convierten a DÍAS en la zona
//     horaria del TRABAJADOR (`timeZone`, src/lib/workerTime.js; por defecto
//     Europe/Madrid), nunca en la del dispositivo. Así una vacación creada a
//     las 00:00 de Madrid (22:00 UTC del día anterior) cuenta los mismos
//     días en un móvil en GMT que en uno en Madrid.
//
// HORARIO DESCONOCIDO (auditoría 2026-09-21): si para una fecha no se puede
// resolver NINGÚN horario (workhours vacíos, histórico aún sin cargar,
// petición fallida…), antes todos los días parecían descanso y el arrastre de
// NATURALES corría hasta el tope de 60 días (184 días para una trabajadora
// con 24 reales). Ahora, sin horario conocido:
//   - NATURALES → NO se aplica arrastre: solo cuentan los días solicitados.
//   - LABORABLES → no se puede saber qué día es laboral: esos días no cuentan.
// En ambos casos el resultado lleva `scheduleUnknown: true` para que las
// pantallas muestren "—" / "cargando" en lugar de una cifra equivocada.
//
// CÓMPUTO POR AÑO: `countPeriods` acepta `from` / `to` ('YYYY-MM-DD',
// inclusivos) y cuenta SOLO los días de los periodos que caen dentro. Los
// periodos se emparejan y se calculan enteros (con su arrastre) y después se
// recortan: un 28-dic → 3-ene cuenta 4 días en su año y 3 en el siguiente.
// Para eso las pantallas cargan los eventos con margen (`yearCountWindow`).

import { makeSlotResolver, slotsAtDate } from './timeline';
import {
  DEFAULT_TIME_ZONE, dayKeyInZone, dayKeysBetween, addDaysToDayKey, todayInZone,
} from './workerTime';

// `workHoursByDow` puede ser:
//   - Map<dow, slot>          → mismo horario para todo el histórico (legacy)
//   - Array de ScheduleVersion → resuelve el horario vigente en cada fecha
//   - función (date) => slot   → resolver custom
//   - null/undefined          → no hay info, ningún día es laboral
// El resolver acepta los 4 casos y siempre devuelve el slot vigente en la
// fecha ('YYYY-MM-DD') que le pasemos.
const isLaboralForWorkerAt = (iso, slotResolver) => {
  const slot = slotResolver(iso);
  if (!slot) return false;
  // El slot puede traer in1 como "08:00:00" o null. Cualquier truthy cuenta.
  return !!slot.in1;
};

const isHoliday = (iso, holidaysSet) =>
  !!(holidaysSet && holidaysSet.has && holidaysSet.has(iso));

const hasWorkingSlot = (slots) => {
  for (const s of slots || []) {
    if (s && s.in1) return true;
  }
  return false;
};

// ¿Se conoce el horario del trabajador en la fecha `iso` ('YYYY-MM-DD')?
// "Conocido" = hay al menos UN día laboral (slot con `in1`) en el horario
// vigente en esa fecha. Un horario sin ningún día laboral no es un horario:
// es que no se ha cargado (o no existe) y no se puede decidir qué es descanso.
//   - null / undefined            → desconocido.
//   - Array (ScheduleVersion[])   → la versión vigente en `iso` tiene slots laborales.
//   - Map<dow, slot> / objeto     → algún slot laboral.
//   - función (resolver custom)   → se asume conocido (no hay forma de saberlo).
export const hasScheduleAt = (workHoursByDow, iso, timeZone = DEFAULT_TIME_ZONE) => {
  const input = workHoursByDow;
  if (input == null) return false;
  if (typeof input === 'function') return true;
  if (Array.isArray(input)) return hasWorkingSlot(slotsAtDate(input, iso, timeZone));
  if (typeof input.values === 'function') return hasWorkingSlot(input.values());
  return hasWorkingSlot(Object.values(input));
};

// Versión detallada de computePeriodDays: devuelve
//   { days: Set<ISO>, unknownDays: Set<ISO> }
// `unknownDays` = días en los que hacía falta el horario y no se conocía
// (laborables: días del rango; naturales: el día donde empezaría el arrastre).
export const computePeriodDaysDetailed = ({
  start,
  end,
  holidayType = 'laborables',
  workHoursByDow = null,
  holidaysSet = null,
  // Cap defensivo: en NATURALES, si por error de datos no encontramos un
  // día laboral del trabajador en el arrastre, paramos para no caer en
  // bucle infinito. 60 días cubre cualquier caso humano razonable.
  maxCarryoverDays = 60,
  // Zona del trabajador (timeZoneForWorker). Decide a qué día pertenecen
  // los instantes start/end.
  timeZone = DEFAULT_TIME_ZONE,
}) => {
  const out = new Set();
  const unknownDays = new Set();
  const result = { days: out, unknownDays };
  if (!start || !end) return result;
  const startKey = dayKeyInZone(start, timeZone);
  const endKey = dayKeyInZone(end, timeZone);
  if (!startKey || !endKey || startKey > endKey) return result;

  const naturals = holidayType === 'festivos';
  // Normalizamos la entrada: ahora `workHoursByDow` puede ser un histórico
  // de versiones (ScheduleVersion[]) y el resolver decide en cada fecha
  // qué horario aplicar. Si se pasa un Map clásico, se trata como horario
  // constante (backward-compat).
  const resolveSlot = makeSlotResolver(workHoursByDow, timeZone);
  const known = (iso) => hasScheduleAt(workHoursByDow, iso, timeZone);

  // 1) Días dentro del rango solicitado.
  for (const iso of dayKeysBetween(startKey, endKey)) {
    if (naturals) {
      out.add(iso);
    } else if (!known(iso)) {
      // Laborables sin horario: no se puede decidir si es laboral.
      unknownDays.add(iso);
    } else {
      // Laborables: solo días laborales del trabajador y no festivos.
      const laboral = isLaboralForWorkerAt(iso, resolveSlot);
      const festivo = isHoliday(iso, holidaysSet);
      if (laboral && !festivo) {
        out.add(iso);
      }
    }
  }

  // 2) Arrastre solo para naturales: días post-fin que sean no laborables
  //    para el trabajador (descansos, findes sin jornada, festivos) hasta
  //    encontrar el primer día laboral no festivo del trabajador. Sin
  //    horario conocido NO hay arrastre (se marca el día como desconocido).
  if (naturals) {
    let cur = addDaysToDayKey(endKey, 1);
    let carry = 0;
    while (carry < maxCarryoverDays) {
      if (!known(cur)) {
        unknownDays.add(cur);
        break;
      }
      const laboral = isLaboralForWorkerAt(cur, resolveSlot);
      const festivo = isHoliday(cur, holidaysSet);
      if (laboral && !festivo) break;
      out.add(cur);
      cur = addDaysToDayKey(cur, 1);
      carry += 1;
    }
  }

  return result;
};

// Calcula el set de días ISO consumidos por UN solo periodo (start..end).
// Se exporta para tests y para consumo individual; `countPeriods` orquesta
// múltiples periodos deduplicando solapamientos.
export const computePeriodDays = (args) => computePeriodDaysDetailed(args).days;

const inRange = (iso, from, to) => (!from || iso >= from) && (!to || iso <= to);

// Une el cómputo de varios periodos. Devuelve
//   { days: Set<ISO> deduplicado, count, scheduleUnknown }.
// `opts` = opciones de computePeriodDays (holidayType, workHoursByDow,
// holidaysSet, timeZone…) más `from` / `to` opcionales ('YYYY-MM-DD',
// inclusivos): solo cuentan los días dentro de ese rango (p.ej. el año).
// `scheduleUnknown` es true si algún día del rango necesitaba el horario y no
// se conocía: la cifra NO es fiable y la UI debe mostrar "—".
export const countPeriods = (periods, opts = {}) => {
  const { from = null, to = null } = opts;
  const merged = new Set();
  let scheduleUnknown = false;
  for (const p of periods || []) {
    const { days, unknownDays } = computePeriodDaysDetailed({ ...opts, start: p.start, end: p.end });
    for (const iso of days) {
      if (inRange(iso, from, to)) merged.add(iso);
    }
    for (const iso of unknownDays) {
      if (inRange(iso, from, to)) scheduleUnknown = true;
    }
  }
  return { days: merged, count: merged.size, scheduleUnknown };
};

// Cuántos días de un Set<ISO> caen en [from, to] (inclusivo). Útil para
// contadores por año sobre sets construidos con eventos de margen.
export const countDaysInRange = (daysSet, from, to) => {
  let n = 0;
  for (const iso of daysSet || []) {
    if (inRange(iso, from, to)) n += 1;
  }
  return n;
};

// Ventana de un contador ANUAL. `from`/`to` = el año natural; `fetchFrom` /
// `fetchTo` = rango de eventos a pedir, con `marginDays` de margen a cada
// lado para no perder los periodos que cruzan el cambio de año (un 28-dic →
// 3-ene tiene el START en un año y el END en otro) ni el arrastre.
export const YEAR_COUNT_MARGIN_DAYS = 60;
export const yearCountWindow = (year, marginDays = YEAR_COUNT_MARGIN_DAYS) => {
  const y = String(Number(year)).padStart(4, '0');
  const from = `${y}-01-01`;
  const to = `${y}-12-31`;
  return {
    from,
    to,
    fetchFrom: addDaysToDayKey(from, -marginDays),
    fetchTo: addDaysToDayKey(to, marginDays),
  };
};

// Parte un Set de días ISO en los ya transcurridos y los que están por venir.
//
// Sirve para separar dos conceptos que NO son lo mismo y que hasta ahora se
// mostraban bajo la misma etiqueta «disfrutadas»:
//   - DISFRUTADAS  → aprobadas Y ya vividas. Días realmente gastados.
//   - PROGRAMADAS  → aprobadas pero aún futuras. Están concedidas y descuentan
//                    del cupo, pero el trabajador todavía no las ha disfrutado.
// (Caso Mercedes: en julio se le mostraban 17 «disfrutadas» porque el cómputo
// incluía sus vacaciones aprobadas de agosto, que obviamente no había vivido.)
//
// El día de HOY cuenta como disfrutado: si hoy estás de vacaciones, lo estás
// gastando. La comparación es lexicográfica, válida para "YYYY-MM-DD".
// Sin `todayIso`, "hoy" es el de la zona del trabajador (`timeZone`).
export const splitDaysByToday = (daysSet, todayIso, timeZone = DEFAULT_TIME_ZONE) => {
  const today = todayIso || todayInZone(timeZone);
  let pastCount = 0;
  let futureCount = 0;
  for (const iso of daysSet || []) {
    if (iso <= today) pastCount += 1;
    else futureCount += 1;
  }
  return { pastCount, futureCount };
};

// Normaliza el `workhours` que devuelve el backend (array por dayOfWeek
// donde dayOfWeek 0=Dom..6=Sáb) a un Map<dow, slot> para consulta O(1).
// Tolera tanto formato array como ya-Map.
export const buildWorkHoursByDow = (workhoursList) => {
  const m = new Map();
  if (!Array.isArray(workhoursList)) return m;
  for (const slot of workhoursList) {
    if (slot && slot.dayOfWeek != null) {
      m.set(Number(slot.dayOfWeek), slot);
    }
  }
  return m;
};
